import ContentCopy from "@mui/icons-material/ContentCopy";
import Delete from "@mui/icons-material/Delete";
import Description from "@mui/icons-material/Description";
import Edit from "@mui/icons-material/Edit";
import FileCopy from "@mui/icons-material/FileCopy";
import MoreVert from "@mui/icons-material/MoreVert";
import { openPath } from "@tauri-apps/plugin-opener";
import cx from "classnames";
import { type FC, useCallback, useState } from "react";

import type { PageMetadata } from "@moment/api-collab/api-types";
import { DropdownMenu } from "@moment/design-system/DropdownMenu";

import { copyContent } from "~/components/common/CopyText";
import { useDeleteConfirmModal } from "~/components/common/useDeleteConfirmModal";
import { getPageDirPath } from "~/data/filesystem";
import { useRouteParams } from "~/data/route";
import { usePageActor } from "~/state";
import { IconButton } from "~/ui/Button";

import { useCurrentDocument } from "../hooks/useCurrentDocument";

const DROPDOWN_ICON_PROPS = { className: "mr-2 shrink-0" };

export type PagePopOverProps = {
	page: PageMetadata;
	renamingPage: boolean;
	onRenamePage: (pageId: string) => void;
	allowDelete?: boolean;
};

export const PagePopOver: FC<PagePopOverProps> = ({
	page,
	renamingPage: renamingPage,
	onRenamePage,
	allowDelete,
}) => {
	const { documentId } = useRouteParams();
	const pageActor = usePageActor(documentId, page.id);

	const togglePageArchived = () => {
		if (page.archived) {
			pageActor?.send({
				type: "page.unarchived",
			});
		} else {
			pageActor?.send({
				type: "page.archived",
			});
		}
	};

	const document = useCurrentDocument();
	const { deleteConfirmation } = useDeleteConfirmModal();
	const [open, setOpen] = useState(false);

	// Variables
	// TODO(trey): implement page duplication
	const canDuplicatePage = false;
	// const canDuplicatePage = canvasTransforms?.duplicatePage !== undefined;

	// Functions
	const handleArchivePage = async () => {
		if (!document?.id || !page.id) {
			return;
		}

		const confirmStatus = await deleteConfirmation({
			headingTitle: "Archive page",
			name: `the page ${page.title || "untitled"}`,
			action: "archive",
			confirmLabel: "Archive",
		});

		if (confirmStatus) {
			void togglePageArchived();
		}
	};
	const handleDuplicatePage = async () => {
		// const title = `Copy of ${page.title}`;
		// TODO(trey): implement page duplication
		// await canvasTransforms?.duplicatePage?.(page.id, userID, title);
	};
	const copyPageId = () => {
		if (!page.id) {
			return;
		}
		void copyContent(page.id, "Copied Page ID to clipboard");
	};
	const copyPagePath = useCallback(async () => {
		const path = await getPageDirPath(documentId, page.id);
		if (!path) {
			return;
		}
		void copyContent(path, "Copied page path to clipboard");
	}, [documentId, page.id]);

	return (
		<DropdownMenu.Root open={open} onOpenChange={setOpen}>
			<DropdownMenu.Trigger>
				<IconButton
					preset="quaternary"
					size="xs"
					toggled={open}
					className={cx("!bg-transparent", "mr-1", "hover:flex", "group-hover:flex", {
						flex: open || renamingPage,
						hidden: !open && !renamingPage,
					})}
					onClick={(e) => e.stopPropagation()}
					label="Show page overflow menu"
					icon={<MoreVert />}
				/>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content className="mb-2" align="start">
					{/* TODO: I would like to add these. */}
					{/* <DropdownMenu.Item>
								<DocumentAdd size={16} {...DROPDOWN_ICON_PROPS} />
								Add subpage
							</DropdownMenu.Item>
							<DropdownMenu.Item>
								<Add size={16} {...DROPDOWN_ICON_PROPS} />
								Add page below
							</DropdownMenu.Item>

							<DropdownMenu.Separator /> */}
					<DropdownMenu.Item onSelect={() => onRenamePage(page.id)}>
						<Edit {...DROPDOWN_ICON_PROPS} />
						Rename page
					</DropdownMenu.Item>

					{canDuplicatePage && (
						<DropdownMenu.Item onSelect={() => handleDuplicatePage()}>
							<FileCopy {...DROPDOWN_ICON_PROPS} />
							Duplicate page
						</DropdownMenu.Item>
					)}

					<DropdownMenu.Separator />

					<DropdownMenu.Item onSelect={copyPageId}>
						<ContentCopy {...DROPDOWN_ICON_PROPS} />
						Copy page ID
					</DropdownMenu.Item>

					<DropdownMenu.Item
						onSelect={async () => {
							const path = await getPageDirPath(documentId, page.id);
							if (!path) {
								return;
							}
							await openPath(path);
						}}
					>
						<Description />
						<span>Reveal page on filesystem…</span>
					</DropdownMenu.Item>

					<DropdownMenu.Item onSelect={copyPagePath}>
						<FileCopy {...DROPDOWN_ICON_PROPS} />
						Copy path to page
					</DropdownMenu.Item>

					<DropdownMenu.Separator />
					<DropdownMenu.Item
						variant="negative"
						disabled={!allowDelete}
						onSelect={() => handleArchivePage()}
					>
						<Delete {...DROPDOWN_ICON_PROPS} />
						Archive page
					</DropdownMenu.Item>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
};
